import React from "react";
import { useLocation } from "react-router-dom";

const origin = "https://observertheory.com";

const routeCopy: Record<string, { title: string; description: string }> = {
  "/": {
    title:
      "Observer Theory | Manifestation, Spiritual & Psychic Practitioner Records",
    description:
      "Research manifestation and law of attraction coaches, law of assumption teachers, tarot readers, psychics, mediums, astrologers, spiritual teachers, and energy practitioners.",
  },
  "/search": {
    title: "Search Spiritual Teachers, Readers & Coaches | Observer Theory",
    description:
      "Search by name for sourced records on manifestation coaches, law of attraction and law of assumption teachers, tarot readers, psychics, mediums, astrologers, and spiritual practitioners.",
  },
  "/practitioners": {
    title: "Browse Spiritual Practitioners by Practice | Observer Theory",
    description:
      "Browse sourced Tier A factual records for manifestation coaches, tarot readers, psychics, mediums, astrologers, energy practitioners, and spiritual teachers.",
  },
  "/write-review": {
    title: "Review a Spiritual Practitioner | Observer Theory",
    description:
      "Share a firsthand experience with a manifestation coach, spiritual teacher, tarot reader, psychic, medium, astrologer, energy practitioner, course, reading, or coaching service.",
  },
  "/report": {
    title: "Submit or Report a Spiritual Practice | Observer Theory",
    description:
      "Nominate a practitioner, submit your spiritual or transformational practice, suggest a factual correction, or report a concern to Observer Theory.",
  },
  "/claim": {
    title: "Claim Your Practitioner Profile | Observer Theory",
    description:
      "Verify control of an official website or channel and claim your manifestation, spiritual teaching, tarot, psychic, mediumship, astrology, or energy-practice profile.",
  },
  "/trust": {
    title: "Trust Centre | Observer Theory",
    description:
      "Learn how Observer Theory sources practitioner records, classes reviews, protects evidence, handles disputes, and keeps rankings independent.",
  },
  "/trust/ranking": {
    title: "How Practitioner Search Is Ranked | Observer Theory",
    description:
      "See what can and cannot affect search ordering for spiritual teachers, readers, coaches, psychics, mediums, astrologers, and other practitioners.",
  },
  "/trust/reviews": {
    title: "Spiritual Practitioner Review Policy | Observer Theory",
    description:
      "How verified-purchase, unverified-client, and free-content opinions are separated for coaching, readings, courses, communities, and spiritual services.",
  },
  "/trust/disputes": {
    title: "Review Disputes, Corrections & Appeals | Observer Theory",
    description:
      "How practitioners and reviewers can use Observer Theory’s free reply, dispute, factual correction, and appeal processes.",
  },
  "/trust/seeding": {
    title: "How Opening Practitioner Profiles Are Selected | Observer Theory",
    description:
      "The published methodology for selecting and hand-approving Observer Theory’s opening manifestation, spiritual, psychic, tarot, mediumship, astrology, and coaching profiles.",
  },
  "/about": {
    title: "About Observer Theory | Spiritual Practice Accountability",
    description:
      "Observer Theory is the public accountability record for manifestation, law of attraction, law of assumption, tarot, psychic, mediumship, astrology, energy, and spiritual practices.",
  },
  "/privacy": {
    title: "Privacy Policy | Observer Theory",
    description:
      "How Observer Theory handles account, profile, review, evidence, analytics, and public-source data.",
  },
  "/terms": {
    title: "Terms of Service | Observer Theory",
    description:
      "The terms governing Observer Theory practitioner records, reviews, replies, corrections, disputes, and account use.",
  },
  "/acceptable-use": {
    title: "Acceptable Use | Observer Theory",
    description:
      "Rules for responsible participation in Observer Theory’s spiritual-practitioner accountability record.",
  },
  "/safety": {
    title: "Safety | Observer Theory",
    description:
      "Safety boundaries and reporting guidance for spiritual, manifestation, psychic, reading, coaching, and personal-growth services.",
  },
  "/support": {
    title: "Support | Observer Theory",
    description:
      "Get help with Observer Theory accounts, practitioner profiles, reviews, privacy, corrections, claims, and safety reports.",
  },
  "/account-deletion": {
    title: "Account Deletion | Observer Theory",
    description:
      "How to request deletion or anonymisation of eligible Observer Theory account data.",
  },
  "/sign-in": {
    title: "Sign In | Observer Theory",
    description:
      "Sign in to Observer Theory to review or manage your relationship with a practitioner profile.",
  },
  "/pricing": {
    title: "Observer Plus & Pro Membership | Observer Theory",
    description:
      "Compare Observer Theory Free, Plus, and Pro memberships for private saved research, notes, collections, and practitioner analytics tools.",
  },
  "/library": {
    title: "Private Research Library | Observer Theory",
    description:
      "Save practitioner records and keep account-private research notes without affecting public rankings or reviews.",
  },
};

const upsertMeta = (selector: string, attributes: Record<string, string>) => {
  let node = document.head.querySelector<HTMLMetaElement>(selector);
  if (!node) {
    node = document.createElement("meta");
    document.head.appendChild(node);
  }
  Object.entries(attributes).forEach(([key, value]) =>
    node!.setAttribute(key, value),
  );
};

const RouteMetadata: React.FC = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    if (pathname.startsWith("/profile/")) return;
    const meta = routeCopy[pathname] || routeCopy["/"];
    document.title = meta.title;
    upsertMeta('meta[name="description"]', {
      name: "description",
      content: meta.description,
    });
    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: "index,follow",
    });
    upsertMeta('meta[property="og:title"]', {
      property: "og:title",
      content: meta.title,
    });
    upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: meta.description,
    });
    upsertMeta('meta[property="og:url"]', {
      property: "og:url",
      content: origin + pathname,
    });
    upsertMeta('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: meta.title,
    });
    upsertMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: meta.description,
    });
    let canonical = document.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]',
    );
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = origin + pathname;
  }, [pathname]);

  return null;
};

export default RouteMetadata;
